import React, { useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Link, useParams } from 'react-router-dom';

const API_BASE = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
const NOT_FOUND = 'Test device not found. It may have been reset or belong to another operator.';

export default function LaserTestDevicePage() {
  const auth = useAuth();
  const { id } = useParams();
  return <LaserTestDeviceSession key={`${auth.userId || 'anonymous'}:${auth.isSignedIn ? 'signed-in' : 'signed-out'}:${id}`} auth={auth} id={id} />;
}

function LaserTestDeviceSession({ auth, id }) {
  const { getToken, isLoaded, isSignedIn, userId } = auth;
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!isLoaded) return undefined;
    const controller = new AbortController();
    let active = true;
    async function load() {
      setLoading(true);
      setRecord(null);
      setError('');
      try {
        if (!isSignedIn || !userId) throw new Error('Sign in to open this test device.');
        if (!/^[0-9a-f]{24}$/.test(id || '')) throw new Error(NOT_FOUND);
        const token = await getToken();
        if (!active) return;
        if (!token) throw new Error('Your session has expired. Sign in again to open this test device.');
        const response = await window.fetch(`${API_BASE}/quality/laser-engraving/test/records/${encodeURIComponent(id)}`, {
          headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
        });
        if (!active) return;
        if (response.status === 401) throw new Error('Your session has expired. Sign in again to open this test device.');
        if (response.status === 403 || response.status === 404) throw new Error(NOT_FOUND);
        if (!response.ok) throw new Error('The test device could not be loaded. Try again.');
        const data = await response.json();
        if (!active) return;
        const saved = data.record;
        if (saved?.isSandbox !== true || saved.family !== 'test' || saved.component !== 'MON' || saved.id !== id || saved.deviceId !== id
          || typeof saved.serviceTagNumber !== 'string' || !saved.serviceTagNumber.startsWith('TEST-') || typeof saved.productNumber !== 'string' || !saved.productNumber) {
          throw new Error('The saved test device identity does not match this link.');
        }
        setRecord(saved);
      } catch (err) {
        if (active && err.name !== 'AbortError') setError(err.message || 'The test device could not be loaded. Try again.');
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => { active = false; controller.abort(); };
  }, [getToken, id, isLoaded, isSignedIn, userId, retry]);

  return (
    <main className="ni-page">
      <div className="ni-shell ni-shell--narrow" style={{ maxWidth: 800, padding: 24 }}>
        <Link to="/quality/laser-engraving?mode=test">← Laser Engraving</Link>
        <section className="ni-panel" style={{ marginTop: 20, padding: 24, borderRadius: 10 }} aria-labelledby="test-device-heading">
          <span style={{ display: 'inline-block', padding: '4px 8px', border: '1px solid var(--ni-green)', borderRadius: 4, fontSize: 12, fontWeight: 700 }}>TEST ONLY</span>
          <h1 id="test-device-heading" style={{ margin: '14px 0 8px', fontSize: 26 }}>Test device</h1>
          <p className="ni-muted">Disposable test device. Production devices and the shared GPV running number are unaffected.</p>
          {loading ? <p role="status">Loading test device…</p> : error ? <div role="alert"><p>{error}</p><button className="ni-button" onClick={() => setRetry(value => value + 1)}>Retry</button></div> : record && <>
            <dl style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', gap: '14px 20px', margin: '26px 0', overflowWrap: 'anywhere' }}>
              <dt>Service tag</dt><dd style={{ margin: 0, fontFamily: 'monospace', userSelect: 'all' }}>{record.serviceTagNumber}</dd>
              <dt>Product P/N</dt><dd style={{ margin: 0, fontFamily: 'monospace', userSelect: 'all' }}>{record.productNumber}</dd>
              <dt>Model</dt><dd style={{ margin: 0 }}>Wave XL · MON</dd>
              <dt>Device ID</dt><dd style={{ margin: 0, fontFamily: 'monospace', userSelect: 'all' }}>{record.deviceId}</dd>
              <dt>Created</dt><dd style={{ margin: 0 }}>{record.createdAt ? new Date(record.createdAt).toLocaleString() : 'Not recorded'}</dd>
            </dl>
            <Link className="ni-button" to={`/quality/laser-engraving?label=${encodeURIComponent(record.id)}`}>Reopen saved label</Link>
            <p className="ni-muted" style={{ fontSize: 13, marginTop: 18 }}>Resetting your Test data in Laser Engraving removes this test device and its saved label.</p>
          </>}
        </section>
      </div>
    </main>
  );
}
